import React, { useState } from 'react';
import logo from '../logo.png';
import {
  Alert,
  Box,
  Button,
  Container,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser, registerUser } from '../services/userService';

const beltRankOptions = [
  { value: '', label: '-- Select Belt Rank --' },
  { value: 'White', label: 'White Belt' },
  { value: 'Orange', label: 'Orange Belt' },
  { value: 'Purple', label: 'Purple Belt' },
  { value: 'Blue', label: 'Blue Belt' },
  { value: 'Green', label: 'Green Belt' },
  { value: 'Brown', label: 'Brown Belt' },
  { value: 'Black', label: 'Black Belt' },
  { value: 'BlackSecondDegree', label: 'Second Degree Black Belt' },
  { value: 'BlackThirdDegree', label: 'Third Degree Black Belt' },
  { value: 'BlackFourthDegree', label: 'Fourth Degree Black Belt' },
  { value: 'BlackFifthDegree', label: 'Fifth Degree Black Belt' },
  { value: 'BlackSixthDegree', label: 'Sixth Degree Black Belt' },
  { value: 'BlackSeventhDegree', label: 'Seventh Degree Black Belt' },
  { value: 'BlackEigthDegree', label: 'Eighth Degree Black Belt' },
  { value: 'BlackNinthDegree', label: 'Ninth Degree Black Belt' },
  { value: 'BlackTenthDegree', label: 'Tenth Degree Black Belt' },
];

function AuthScreen() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [beltRank, setBeltRank] = useState('');
  const [error, setError] = useState(null);

  const toggleMode = () => {
    setIsRegister(!isRegister);
    setEmail('');
    setPassword('');
    setName('');
    setBeltRank('');
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (isRegister) {
      if (!email || !password || !name) {
        setError('Email, password, and name are required');
        return;
      }
      try {
        const response = await registerUser({ email, password, name, beltRank: beltRank || null });
        if (response?.status === 201) {
          navigate('/verify-email', {
            replace: true,
            state: { email },
          });
        }
      } catch (err) {
        setError('Registration error: ' + err.message);
      }
    } else {
      if (!email || !password) {
        setError('Email and password are required');
        return;
      }
      try {
        const result = await loginUser({ email, password });
        await login(result?.accessToken);
      } catch (err) {
        if (err.status === 403 && err.message === 'Email not verified') {
          navigate('/verify-email', {
            replace: true,
            state: { email },
          });
          return;
        }
        setError('Login error: ' + err.message);
      }
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 128,
                height: 128,
                borderRadius: '50%',
                bgcolor: 'common.white',
                p: 1,
              }}
            />
          </Box>

          <Typography variant="h5" component="h1" align="center" fontWeight={700}>
            {isRegister ? 'Create Account' : 'Sign In'}
          </Typography>

          <Fade in>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  required
                  fullWidth
                />
                {!isRegister && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                    <Button
                      component={RouterLink}
                      to="/forgot-password"
                      state={{ email }}
                      variant="text"
                      size="small"
                      sx={{ px: 0 }}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                )}
                {isRegister && (
                  <>
                    <TextField
                      label="Name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="name"
                      required
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel id="belt-rank-label">Belt Rank</InputLabel>
                      <Select
                        labelId="belt-rank-label"
                        value={beltRank}
                        label="Belt Rank"
                        onChange={(e) => setBeltRank(e.target.value)}
                      >
                        {beltRankOptions.map(({ value, label }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <Button type="submit" variant="contained" size="large" fullWidth>
                  {isRegister ? 'Register' : 'Sign In'}
                </Button>
              </Stack>
            </Box>
          </Fade>

          <Button onClick={toggleMode} variant="text">
            {isRegister ? 'Have an account? Sign In' : "Don't have an account? Register"}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            By using this app, you agree to the{' '}
            <Button
              component={RouterLink}
              to="/privacy-policy"
              variant="text"
              size="small"
              sx={{ minWidth: 0, px: 0.5, verticalAlign: 'baseline' }}
            >
              Privacy Policy
            </Button>
            .
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}

export default AuthScreen;
